/**
 * Desktop controls for room screens, docked at the bottom of the window
 * instead of floating in the world. Each RoomScreen reports its distance from
 * the camera; the dock shows the controls for the nearest one in range.
 */
import React, { useState } from 'react';
import { create } from 'zustand';
import { MonitorUp, Link2, Square, Play, Pause, Eye } from 'lucide-react';
import { useWorldStore } from '../stores/worldStore';
import { canStopScreen, controlVideo, openDesktopShare, stopScreen, useRoomScreenStore, useScreenLinkLocked, useWatchingCount } from '../services/roomScreens';
import { canCaptureScreen, isPresenting, startScreenShare, stopScreenShare } from '../services/screenShareService';
import './MediaPanels.css';

const RANGE = 25; // metres: farther than this, no screen controls
const dist = new Map<string, { d: number; isOwner: boolean }>();

export const useScreenDock = create<{ id: string | null; isOwner: boolean }>(() => ({ id: null, isOwner: false }));

function pickNearest() {
  let best: string | null = null, bd = RANGE, owner = false;
  dist.forEach((v, k) => { if (v.d < bd) { bd = v.d; best = k; owner = v.isOwner; } });
  const cur = useScreenDock.getState();
  if (cur.id !== best || cur.isOwner !== owner) useScreenDock.setState({ id: best, isOwner: owner });
}

/** Called by RoomScreen (throttled) with its distance to the camera; null removes it. */
export function reportScreenDistance(id: string, d: number | null, isOwner = false) {
  if (d === null) dist.delete(id); else dist.set(id, { d, isOwner });
  pickNearest();
}

function hostOf(url: string) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

export default function RoomScreenDock() {
  const { id, isOwner } = useScreenDock();
  const screen = useRoomScreenStore((s) => (id ? s.screens[id] : undefined));
  const myId = useWorldStore((s) => s.user?.id || null);
  const liveShare = useWorldStore((s) => s.activeScreenShare);
  const [err, setErr] = useState('');
  const locked = useScreenLinkLocked();
  // The sharer of a cloud browser sees how many accounts they're paying for.
  const mineBrowser = !!screen && screen.mode === 'browser' && !!myId && screen.startedBy === myId;
  const watching = useWatchingCount(id, mineBrowser);
  if (!id) return null;

  const presenting = !!liveShare && isPresenting();
  const canStop = !!screen && canStopScreen(screen, isOwner, myId);
  const shareMyScreen = async () => {
    document.exitPointerLock?.();
    setErr('');
    try {
      if (screen) {
        if (!canStop) throw new Error('Someone else’s link is playing here. Use an empty screen, or ask them to stop it.');
        await stopScreen(id);
      }
      await startScreenShare();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not share your screen.';
      // Closing the browser's picker is not an error worth showing.
      if (!/permission denied|notallowed|cancel/i.test(msg)) setErr(msg);
    }
  };
  const status = presenting ? 'You’re sharing your screen'
    : screen ? `${screen.mode === 'video' ? 'Video' : 'Browser'} · ${hostOf(screen.url)}${screen.startedByName ? ` · ${screen.startedByName}` : ''}`
    : liveShare ? 'Someone is sharing their screen'
    : '';

  return <div className="room-screen-dock" onPointerDown={(e) => e.stopPropagation()}>
    <div className="room-screen-dock__status">
      <span className={`room-screen-dock__dot${screen || liveShare ? ' is-live' : ''}`} />
      <span className="room-screen-dock__label">Screen</span>
      {(err || status) && <span className={`room-screen-dock__text${err ? ' is-error' : ''}`} title={err || status}>{err || status}</span>}
    </div>
    <div className="room-screen-dock__buttons">
      {screen?.mode === 'video' && screen.video && !!myId && <>
        <button onClick={() => controlVideo(id, { playing: !screen.video!.playing })} title={screen.video.playing ? 'Pause' : 'Play'}>
          {screen.video.playing ? <Pause size={15} /> : <Play size={15} />}
        </button>
        <button onClick={() => controlVideo(id, { seekBy: -10 })}>−10s</button>
        <button onClick={() => controlVideo(id, { seekBy: 10 })}>+10s</button>
      </>}
      {mineBrowser && watching !== null && <span className="room-screen-dock__watching" role="status"
        title="Signed-in viewers in the last 2 minutes (including you). Each one uses your cloud browser minutes; guests are free.">
        <Eye size={14} aria-hidden="true" /> Watching: {watching}
      </span>}
      {canStop && <button onClick={() => void stopScreen(id).catch(() => {})}><Square size={13} /> Stop</button>}
      {/* Non-members see Share link greyed out; clicking opens the dialog's
          Council notice instead of the link field. */}
      <button className={locked ? 'is-locked' : undefined}
        title={locked ? 'Sharing a link to the screen is a Creator Council perk' : undefined}
        aria-label={locked ? `${screen ? 'Share another link' : 'Share link'} (Creator Council perk)` : undefined}
        onClick={() => { document.exitPointerLock?.(); openDesktopShare(id); }}>
        <Link2 size={15} /> {screen ? 'Share another link' : 'Share link'}
        {locked && <span className="room-screen-dock__badge">Council</span>}
      </button>
      {presenting
        ? <button className="is-live" onClick={() => void stopScreenShare()}><Square size={13} /> Stop my screen</button>
        : canCaptureScreen() && !(liveShare && !screen) && <button className="is-primary" onClick={() => void shareMyScreen()}
            title="Share a tab, window or your whole screen — tip: pick a Chrome tab and tick “Share tab audio” for YouTube with sound">
            <MonitorUp size={15} /> Share my screen
          </button>}
    </div>
  </div>;
}
